import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilIcon } from 'lucide-react';
import { apiGet } from '../lib/api.js';
import { formatRelativeTime } from '../lib/format.js';
import { IconButton } from '../components/IconButton.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Callout } from '../ui/Callout.js';
import { Dialog } from '../ui/Dialog.js';
import { useAuth } from '../auth/useAuth.js';
import { DomainForm } from '../groups/DomainForm.js';
import { useUpdateDomain } from '../groups/queries.js';
import { domainKeys, useDomain } from './queries.js';

/** Just the fields this page reads out of the shared liveness projection. */
type LivenessDomain = {
  domainId: number;
  lastSeenAt: string | null;
  lastMissAt: string | null;
};

type FactProps = { term: string; hint?: string; children: ReactNode };

function Fact({ term, hint, children }: FactProps) {
  return (
    <div className="flex flex-col gap-(--space-1) border-t border-rule py-(--space-3) first:border-t-0 sm:flex-row sm:items-baseline sm:gap-(--space-4)">
      <dt className="text-[length:var(--text-small)] text-ink-subtle sm:w-[16ch] sm:shrink-0">
        {term}
      </dt>
      <dd className="flex flex-col gap-(--space-1)">
        {children}
        {hint !== undefined && (
          <p className="max-w-[60ch] text-[length:var(--text-small)] text-ink-subtle">{hint}</p>
        )}
      </dd>
    </div>
  );
}

/**
 * Settings for one hostname.
 *
 * The paths on this domain used to live here in an accordion; they are now
 * pages of their own under /urls. What is left is the configuration that
 * belongs to the hostname itself and has nowhere else to go — its fallback
 * URL, whether it is enabled at all, and whether traffic is actually arriving.
 */
export function DomainScreen() {
  const params = useParams();
  const domainId = Number(params.domainId ?? params.id);
  const client = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin';

  const detail = useDomain(domainId);
  const domain = detail.data?.domain;
  const group = detail.data?.group;
  const updateDomain = useUpdateDomain(group?.id ?? 0);

  /*
   * The same query the liveness projection serves elsewhere, so this page
   * shares that cache entry rather than adding a per-domain endpoint. Misses
   * are only ever recorded against a domain, never against a single path, so
   * this is the only place either figure can honestly be shown.
   */
  const liveness = useQuery({
    queryKey: ['liveness'],
    queryFn: () => apiGet<{ domains: LivenessDomain[] }>('/api/reports/liveness'),
    refetchInterval: 30_000,
  });
  const row = liveness.data?.domains.find((entry) => entry.domainId === domainId);

  const [editing, setEditing] = useState(false);
  const hostname = domain?.hostname ?? '';

  return (
    <section aria-labelledby="domain-heading" className="flex flex-col gap-(--space-5)">
      <header className="flex flex-wrap items-start justify-between gap-(--space-4)">
        <div className="min-w-0">
          {group !== undefined && (
            <Link
              to={`/groups/${group.id}`}
              className="text-[length:var(--text-micro)] uppercase tracking-[0.18em] text-ink-subtle underline-offset-4 hover:underline"
            >
              ← {group.name}
            </Link>
          )}
          <h1
            id="domain-heading"
            className="mt-(--space-1) break-all font-mono text-[length:var(--text-title)] font-bold"
          >
            {hostname || '…'}
          </h1>
          <p className="mt-(--space-2) max-w-[64ch] text-[length:var(--text-small)] font-medium text-ink-muted">
            Settings for this hostname. The URLs configured on it are listed under URLs.
          </p>
        </div>
        {canEdit && domain !== undefined && (
          <IconButton label={`Edit ${hostname}`} onClick={() => setEditing(true)}>
            <PencilIcon aria-hidden="true" />
          </IconButton>
        )}
      </header>

      {detail.isError && (
        <Callout tone="danger">
          {detail.error instanceof Error ? detail.error.message : 'Could not load this domain'}
        </Callout>
      )}

      {domain?.enabled === false && (
        <Callout tone="warn">
          This domain is disabled. Every request arriving on {hostname} goes to the global
          fallback, whatever its URLs are configured to do.
        </Callout>
      )}

      {domain !== undefined && (
        <dl className="rounded-(--radius-lg) border border-rule bg-surface-raised px-(--space-4) py-(--space-2) shadow-(--shadow-raised)">
          <Fact term="Status">
            {/* Worded to match the control that sets it: the flag comes from a
                checkbox labelled "Enabled" on the domain form. */}
            <StatusBadge
              status={domain.enabled ? 'active' : 'paused'}
              label={domain.enabled ? 'Enabled' : 'Disabled'}
            />
          </Fact>

          <Fact
            term="Fallback URL"
            hint="Where a request on this hostname goes when no URL matches it, or when the domain is disabled. Without one, the global fallback is used."
          >
            {domain.fallbackUrl === null ? (
              <span className="text-[length:var(--text-small)] text-ink-subtle">
                Global fallback
              </span>
            ) : (
              <span className="break-all font-mono text-[length:var(--text-small)] text-ink">
                {domain.fallbackUrl}
              </span>
            )}
          </Fact>

          <Fact
            term="Last redirect"
            hint="A hostname that has never been seen is usually DNS, an ingress host rule or a certificate that does not cover it — not a weighting mistake."
          >
            <span
              className={
                row?.lastSeenAt == null
                  ? 'text-[length:var(--text-small)] text-warn'
                  : 'text-[length:var(--text-small)] text-ink'
              }
            >
              {formatRelativeTime(row?.lastSeenAt ?? null)}
            </span>
          </Fact>

          <Fact
            term="Last miss"
            hint="A request that reached this hostname and matched none of its URLs. Misses are recorded against the domain, not against a single URL."
          >
            <span className="text-[length:var(--text-small)] text-ink-muted">
              {row?.lastMissAt == null ? '—' : formatRelativeTime(row.lastMissAt)}
            </span>
          </Fact>
        </dl>
      )}

      <Dialog open={editing} title="Edit domain" onClose={() => setEditing(false)}>
        {domain !== undefined && (
          <DomainForm
            initial={{
              hostname: domain.hostname,
              fallbackUrl: domain.fallbackUrl,
              enabled: domain.enabled,
            }}
            submitLabel="Save domain"
            onCancel={() => setEditing(false)}
            onSubmit={async (input) => {
              await updateDomain.mutateAsync({ id: domain.id, input });
              // `useUpdateDomain` invalidates the group listing it was written
              // for; this page reads the domain on its own key.
              await client.invalidateQueries({ queryKey: domainKeys.one(domainId) });
              setEditing(false);
            }}
          />
        )}
      </Dialog>
    </section>
  );
}
